import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API, setUserToken, setStoredUser } from "@/lib/api";

export default function AuthCallback() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const nav = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    // Auth callback supports TWO providers:
    //  1) First-party Google OAuth  → URL fragment contains `session_token=...`
    //     (backend has already exchanged the code, minted the token, and set
    //     the cookie — we just need to persist it locally and fetch the user).
    //  2) Emergent Google Auth      → URL fragment contains `session_id=...`
    //     (we exchange via POST /api/auth/session).
    const hash = location.hash || window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionToken = params.get("session_token");
    const sessionId = params.get("session_id");

    if (!sessionToken && !sessionId) {
      toast.error("Missing session credentials from Google");
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        let user;
        let token;
        if (sessionToken) {
          // First-party flow: backend already created the session.
          const { data } = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${sessionToken}` },
            withCredentials: true,
          });
          user = data;
          token = sessionToken;
        } else {
          const { data } = await axios.post(
            `${API}/auth/session`,
            {},
            {
              headers: { "X-Session-ID": sessionId },
              withCredentials: true,
            }
          );
          user = data.user;
          token = data.session_token;
        }
        setUserToken(token);
        setStoredUser(user);
        toast.success(`Welcome, ${user.name}`);
        const role = user.role;
        const dest = ["owner", "editor", "viewer"].includes(role) ? "/admin" : "/app";
        nav(dest, { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Google sign-in failed");
        nav("/login", { replace: true });
      }
    })();
  }, [location.hash, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-grid">
      <div
        data-testid="auth-callback-loading"
        className="flex flex-col items-center gap-3 text-muted-foreground"
      >
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <div className="text-sm font-semibold">Completing sign-in…</div>
      </div>
    </div>
  );
}
